import { CheckIcon, ChevronsUpDownIcon, Grid3x3, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { FilterToggleGroup } from "@/components/shared/filter-toggle-group";
import { SearchInput } from "@/components/shared/search-input";
import { Command, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { type MatrixCapability, useRoleMatrix } from "@/hooks/use-role-matrix";
import { cn } from "@/lib/utils";

const UNCATEGORIZED = "__uncategorized__";

/**
 * Category filter — a plain multi-select over the distinct category names
 * already present in the loaded matrix data. No server round-trip: unlike
 * `EntityCombobox` this never needs to search beyond what's on screen, since
 * the matrix has already fetched every capability.
 *
 * A listbox rather than `FilterToggleGroup`'s pills — Skills/Competencies
 * only ever has two options, but categories is open-ended (an admin can add
 * as many as they like), and a row of pills stops scaling well well before a
 * dropdown does (Matt, 2026-10-15 — reversing the 2026-10-14 switch to pills
 * for this one filter specifically; Skills/Competencies keeps its pills).
 */
function CategoryFilter({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = new Set(value);

  function toggle(v: string) {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  }

  const label =
    value.length === 0
      ? "All categories"
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ?? "1 category")
        : `${value.length} categories`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="border-input flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm font-normal shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] dark:bg-input/30 sm:w-48"
        >
          <span className={cn("truncate", value.length === 0 && "text-muted-foreground")}>
            {label}
          </span>
          <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0" style={{ width: "var(--radix-popover-trigger-width)" }}>
        <Command>
          <CommandInput placeholder="Filter categories…" />
          <CommandList>
            {options.map((opt) => (
              <CommandItem key={opt.value} value={opt.label} onSelect={() => toggle(opt.value)}>
                <CheckIcon
                  className={cn("size-4", selected.has(opt.value) ? "opacity-100" : "opacity-0")}
                />
                {opt.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Filled-cell colors, by capability kind. Both are drawn from the brand
// `--chart-*` series ramp (cyan vs indigo); they read as distinct here because
// every cell is also anchored by its row + column text labels and the legend —
// color is never the sole channel.
const KIND_COLOR: Record<MatrixCapability["kind"], string> = {
  skill: "var(--chart-1)",
  competency: "var(--chart-3)",
};

const KIND_OPTIONS = [
  { value: "skill", label: "Skills" },
  { value: "competency", label: "Competencies" },
];

function LoadingGrid() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-9 w-72" />
      <Skeleton className="h-[420px] w-full rounded-lg" />
    </div>
  );
}

/**
 * Role × Capability requirement matrix. Roles are rows (sticky left column),
 * capabilities are columns (vertical, sticky header, grouped skills-then-
 * competencies); a filled cell means the role requires that capability. Scrolls
 * horizontally inside its own overflow container so it stays readable at ~30
 * roles × ~40 capabilities; hovering a cell highlights its row and column.
 *
 * Accessibility: it is a real `<table>` with `<th scope>` headers and a caption
 * — the semantic data table IS the text alternative. Each cell carries sr-only
 * "Required" / "Not required" text and a hover `title`, so the fill is
 * decorative only.
 */
export function RoleCapabilityMatrix() {
  const { data, isLoading, isError, error, refetch } = useRoleMatrix();
  const [search, setSearch] = useState("");
  const [roleSearch, setRoleSearch] = useState("");
  const [kinds, setKinds] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  /**
   * Hover highlighting is done in CSS and the DOM, NOT React state.
   *
   * It used to be two `useState`s, which meant every mouse move over the
   * grid re-rendered all ~2,500 cells: measured at 221ms per hover, enough
   * to saturate the main thread so that clicking a header felt slow even
   * though the click itself takes 64ms.
   *
   * The row highlight is now pure CSS (`:hover` on the row). The column
   * highlight needs JS — CSS has no column selector — but it toggles a
   * class on the ~45 cells of one column via delegation, which is
   * sub-millisecond and renders nothing.
   */
  const gridRef = useRef<HTMLDivElement | null>(null);
  const hoverCapRef = useRef<string | null>(null);
  /**
   * Click-to-focus. Picking a capability narrows the ROWS to the roles that
   * require it — "who needs Business Storytelling" is the question a column
   * header invites. Picking a role narrows the COLUMNS to what that role
   * requires, the same question the other way round. They compose, and each
   * clears from the chip above the grid.
   */
  const [focusCap, setFocusCap] = useState<string | null>(null);
  const [focusRole, setFocusRole] = useState<string | null>(null);

  const requiredSet = useMemo(
    () => new Set((data?.required ?? []).map((r) => `${r.roleId}|${r.key}`)),
    [data],
  );

  const categoryOptions = useMemo(() => {
    if (!data) return [];
    const options = data.categories.map((c) => ({ value: c.id, label: c.name }));
    const hasUncategorized = data.capabilities.some((c) => c.categories.length === 0);
    // "No category" rather than "Uncategorized" — an admin can (and one
    // already has) name a REAL category "Uncategorized" as the AC-419
    // fallback for review questions, which would otherwise collide with this
    // synthetic entry for "filed under nothing at all".
    return hasUncategorized
      ? [...options, { value: UNCATEGORIZED, label: "No category" }]
      : options;
  }, [data]);

  const visibleCaps = useMemo(() => {
    if (!data) return [];
    const term = search.trim().toLowerCase();
    return data.capabilities.filter(
      (c) =>
        (kinds.length === 0 || kinds.includes(c.kind)) &&
        (categories.length === 0 ||
          (c.categories.length === 0
            ? categories.includes(UNCATEGORIZED)
            : c.categories.some((cat) => categories.includes(cat.id)))) &&
        (term === "" || c.name.toLowerCase().includes(term)) &&
        // A focused role keeps only the capabilities it requires — but the
        // focused capability itself always stays, or clicking a column would
        // make that column disappear.
        (focusRole === null || c.key === focusCap || requiredSet.has(`${focusRole}|${c.key}`)),
    );
  }, [data, search, kinds, categories, focusRole, focusCap, requiredSet]);

  const visibleRoles = useMemo(() => {
    if (!data) return [];
    const term = roleSearch.trim().toLowerCase();
    return data.roles.filter(
      (r) =>
        (term === "" || r.name.toLowerCase().includes(term)) &&
        (focusCap === null || r.id === focusRole || requiredSet.has(`${r.id}|${focusCap}`)),
    );
  }, [data, roleSearch, focusCap, focusRole, requiredSet]);

  // Index of the first competency column — draws a divider between the two
  // capability groups (only when both are present after filtering).
  const firstCompetencyIndex = useMemo(
    () => visibleCaps.findIndex((c) => c.kind === "competency"),
    [visibleCaps],
  );

  /** Paint one column's cells, unpainting whichever was painted before. */
  function highlightColumn(key: string | null): void {
    if (hoverCapRef.current === key) return;
    const root = gridRef.current;
    if (!root) return;
    if (hoverCapRef.current !== null) {
      for (const el of Array.from(
        root.querySelectorAll(`[data-cap="${CSS.escape(hoverCapRef.current)}"]`),
      )) {
        el.classList.remove("bg-accent");
      }
    }
    if (key !== null) {
      for (const el of Array.from(root.querySelectorAll(`[data-cap="${CSS.escape(key)}"]`))) {
        el.classList.add("bg-accent");
      }
    }
    hoverCapRef.current = key;
  }

  if (isLoading) return <LoadingGrid />;
  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load the capability matrix"}
        onRetry={() => refetch()}
      />
    );
  }
  if (!data || data.roles.length === 0) {
    return (
      <EmptyState
        icon={Grid3x3}
        title="No roles yet"
        description="Add roles to the catalog to see which capabilities they require."
      />
    );
  }
  if (data.capabilities.length === 0) {
    return (
      <EmptyState
        icon={Grid3x3}
        title="No requirements defined yet"
        description="Once roles list required skills or competencies, the matrix maps them here."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-52">
          <SearchInput
            value={roleSearch}
            onValueChange={setRoleSearch}
            placeholder="Filter roles…"
          />
        </div>
        <div className="w-full sm:w-52">
          <SearchInput
            value={search}
            onValueChange={setSearch}
            placeholder="Filter capabilities…"
          />
        </div>
        <FilterToggleGroup
          options={KIND_OPTIONS}
          value={kinds}
          onChange={setKinds}
          allLabel="All"
        />
        {categoryOptions.length > 0 && (
          <CategoryFilter options={categoryOptions} value={categories} onChange={setCategories} />
        )}
        <div className="ml-auto flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="size-2.5 rounded-[3px]"
              style={{ backgroundColor: KIND_COLOR.skill }}
            />
            Skill required
          </span>
          <span className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="size-2.5 rounded-[3px]"
              style={{ backgroundColor: KIND_COLOR.competency }}
            />
            Competency required
          </span>
        </div>
      </div>

      {(focusCap !== null || focusRole !== null) && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground text-xs">Focused on</span>
          {focusRole !== null && (
            <FocusChip
              label={data.roles.find((r) => r.id === focusRole)?.name ?? "this role"}
              detail="its requirements only"
              onClear={() => setFocusRole(null)}
            />
          )}
          {focusCap !== null && (
            <FocusChip
              label={data.capabilities.find((c) => c.key === focusCap)?.name ?? "this capability"}
              detail="roles that require it"
              onClear={() => setFocusCap(null)}
            />
          )}
        </div>
      )}

      {visibleCaps.length === 0 || visibleRoles.length === 0 ? (
        <p className="rounded-lg border bg-card px-4 py-8 text-center text-sm text-muted-foreground">
          {visibleRoles.length === 0
            ? "No roles match the current filters."
            : "No capabilities match the current filters."}
        </p>
      ) : (
        /*
          `contain-content` is load-bearing, not tidying. A tall <tbody> inside
          a scroll box escapes it and inflates document.scrollHeight, giving
          the window a real scrollbar over hundreds of blank pixels — measured
          here at 1872px of document on an 800px viewport. Neither
          `overflow: hidden` nor an explicit height fixes it; CSS containment
          is the only thing that collapses it. Don't remove it.

          The height is capped against the VIEWPORT rather than a flat 72vh:
          the page header, tabs, filters and focus chip all sit above the
          grid, so 72vh ran it off the bottom of the screen with nothing
          below it.
        */
        <div
          ref={gridRef}
          onMouseOver={(e) => {
            const cell = (e.target as HTMLElement).closest<HTMLElement>("[data-cap]");
            highlightColumn(cell?.dataset.cap ?? null);
          }}
          // Keyboard parity: tabbing along the header row highlights the
          // column the same way hovering it does.
          onFocus={(e) => {
            const cell = (e.target as HTMLElement).closest<HTMLElement>("[data-cap]");
            highlightColumn(cell?.dataset.cap ?? null);
          }}
          onBlur={() => highlightColumn(null)}
          onMouseLeave={() => highlightColumn(null)}
          className="contain-content max-h-[calc(100dvh-23rem)] overflow-auto rounded-lg border"
        >
          <table className="border-separate border-spacing-0 text-sm">
            <caption className="sr-only">
              Role by capability requirement matrix. Rows are roles; columns are capabilities. A
              filled cell means the role requires that capability.
            </caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="sticky left-0 top-0 z-30 h-40 w-52 min-w-52 border-b border-r bg-card p-2 text-left align-bottom font-semibold"
                >
                  Role
                </th>
                {visibleCaps.map((cap, i) => (
                  <th
                    key={cap.key}
                    scope="col"
                    data-cap={cap.key}
                    className={cn(
                      "sticky top-0 z-20 h-60 w-8 min-w-8 border-b bg-card p-0 align-bottom font-medium",
                      i === firstCompetencyIndex && firstCompetencyIndex > 0 && "border-l-2",
                      focusCap === cap.key && "bg-accent",
                    )}
                  >
                    <button
                      type="button"
                      title={
                        focusCap === cap.key
                          ? `Showing only roles that require ${cap.name} — click to clear`
                          : `Show only roles that require ${cap.name}`
                      }
                      onClick={() => setFocusCap(focusCap === cap.key ? null : cap.key)}
                      className="flex h-full w-full cursor-pointer items-end justify-center pb-2 hover:bg-accent"
                    >
                      {/* 14rem of label room at 11px — measured against the
                          longest real capability name, which needs 211px. The
                          old 8rem at 14px clipped anything past ~21 characters
                          and had no ellipsis to admit it, which is the whole
                          complaint. `text-ellipsis` covers anything longer
                          that gets added later. */}
                      <span
                        className={cn(
                          "max-h-56 overflow-hidden text-ellipsis whitespace-nowrap text-2xs",
                          focusCap === cap.key
                            ? "font-semibold text-foreground"
                            : "text-muted-foreground",
                        )}
                        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                      >
                        {cap.name}
                      </span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRoles.map((role) => (
                // Row highlight, free: no state, no render. The column
                // highlight cannot be done this way — CSS has no column
                // selector — which is why that one is still JS.
                <tr key={role.id} className="[&:hover>*]:bg-accent">
                  <th
                    scope="row"
                    className={cn(
                      "sticky left-0 z-10 w-52 min-w-52 border-b border-r bg-card px-2 py-1.5 text-left font-medium",
                      focusRole === role.id && "bg-accent",
                    )}
                  >
                    <button
                      type="button"
                      title={
                        focusRole === role.id
                          ? `Showing only what ${role.name} requires — click to clear`
                          : `Show only what ${role.name} requires`
                      }
                      onClick={() => setFocusRole(focusRole === role.id ? null : role.id)}
                      className="flex w-full cursor-pointer items-center gap-1.5 text-left"
                    >
                      <span
                        className={cn(
                          "truncate",
                          focusRole === role.id
                            ? "font-semibold text-foreground"
                            : "text-foreground",
                        )}
                      >
                        {role.name}
                      </span>
                      {role.status === "draft" && (
                        <span className="shrink-0 rounded bg-muted px-1 py-0.5 text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Draft
                        </span>
                      )}
                    </button>
                  </th>
                  {visibleCaps.map((cap, i) => {
                    const required = requiredSet.has(`${role.id}|${cap.key}`);
                    return (
                      <td
                        key={cap.key}
                        data-cap={cap.key}
                        title={required ? `${role.name} requires ${cap.name}` : undefined}
                        className={cn(
                          "border-b p-0 text-center",
                          i === firstCompetencyIndex && firstCompetencyIndex > 0 && "border-l-2",
                        )}
                      >
                        <span className="flex h-8 items-center justify-center">
                          {required ? (
                            <span
                              aria-hidden="true"
                              className="size-4 rounded-[3px]"
                              style={{ backgroundColor: KIND_COLOR[cap.kind] }}
                            />
                          ) : (
                            <span aria-hidden="true" className="size-1 rounded-full bg-border" />
                          )}
                          <span className="sr-only">{required ? "Required" : "Not required"}</span>
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * A click-to-focus selection, shown above the grid so it is obvious why the
 * matrix shrank — and clearable without having to find the header that set
 * it.
 */
function FocusChip({
  label,
  detail,
  onClear,
}: {
  label: string;
  detail: string;
  onClear: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border bg-accent px-2.5 py-1 text-xs">
      <span className="font-medium text-foreground">{label}</span>
      <span className="text-muted-foreground">— {detail}</span>
      <button
        type="button"
        onClick={onClear}
        aria-label={`Clear focus on ${label}`}
        className="cursor-pointer rounded-full text-muted-foreground transition-colors hover:text-foreground"
      >
        <X className="size-3.5" />
      </button>
    </span>
  );
}
